import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Plus, Minus, MessageCircle, Phone, Mail } from 'lucide-react';
import SEO from '../components/ui/SEO';

const FAQ: React.FC = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.3, triggerOnce: true });
  const [faqRef, faqInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [openItems, setOpenItems] = useState<number[]>([0]);

  const faqCategories = [
    {
      title: 'General Services',
      faqs: [
        {
          question: 'What services does Shubham Pratap Singh Architect provide?',
          answer:
            'We offer comprehensive architectural services including 3D elevation design, interior design, structural design, landscape design, and complete project management. Our team handles everything from initial concept to final construction supervision.',
        },
        {
          question: 'How long does the design process typically take?',
          answer:
            'The timeline varies depending on the project scope. Simple residential designs take 2-3 weeks, while complex commercial projects may require 6-8 weeks. We provide detailed timelines during our initial consultation and keep you updated throughout the process.',
        },
        {
          question: 'Do you work on both residential and commercial projects?',
          answer:
            'Yes, we specialize in both residential and commercial architecture. Our portfolio includes luxury homes, apartment complexes, office buildings, retail spaces, restaurants, and institutional projects. We adapt our design approach to meet the specific needs of each project type.',
        },
        {
          question: 'What makes Shubham Pratap Singh Architect different from other firms?',
          answer:
            'Our unique combination of innovative design, technical expertise, and personalized service sets us apart. We use cutting-edge 3D visualization technology, maintain transparent communication, and ensure every project reflects our commitment to quality and client satisfaction.',
        },
      ],
    },
    {
      title: 'Design & Planning',
      faqs: [
        {
          question: 'Do you provide 3D visualizations of the design?',
          answer:
            'Absolutely! We create photorealistic 3D renderings and virtual walkthroughs for all our projects. This helps you visualize the final result before construction begins and allows for any modifications during the design phase.',
        },
        {
          question: 'Can I make changes to the design after approval?',
          answer:
            'Minor changes can be accommodated during the early construction phase. However, major modifications may require additional approvals and costs. We encourage thorough review of designs before approval to minimize changes later.',
        },
        {
          question: 'How do you ensure designs are sustainable and energy-efficient?',
          answer:
            'We incorporate sustainable design principles including natural lighting, cross-ventilation, energy-efficient materials, rainwater harvesting, and solar panel integration. Our goal is to create environmentally responsible buildings that reduce long-term operating costs.',
        },
      ],
    },
    {
      title: 'Costs & Payment',
      faqs: [
        {
          question: 'How do you calculate project costs?',
          answer:
            'Our fees are based on project complexity, scope of work, and timeline. For design services, we typically charge a percentage of the construction cost or a fixed fee. Specific services have separate pricing. We provide detailed cost breakdowns during consultation.',
        },
        {
          question: 'Do you offer payment plans?',
          answer:
            'Yes, we offer flexible payment plans tied to project milestones. Typically, we require an initial deposit to begin work, with remaining payments distributed across design phases, approvals, and project completion stages.',
        },
        {
          question: 'Are there any hidden costs I should know about?',
          answer:
            'We believe in complete transparency. All costs including authority charges and additional services are clearly outlined in our proposal. We inform you of any potential additional costs upfront and get approval before proceeding.',
        },
        {
          question: 'What happens if the project goes over budget?',
          answer:
            'We provide detailed cost estimates and work diligently to stay within budget. If unforeseen circumstances arise, we discuss all options with you before making any decisions. Our project management approach focuses on cost control throughout the process.',
        },
      ],
    },
    {
      title: 'Project Management',
      faqs: [
        {
          question: 'Do you supervise construction work?',
          answer:
            'Yes, we offer construction supervision services to ensure your project is built according to approved plans and specifications. Our team conducts regular site visits, quality checks, and coordinates with contractors to maintain standards.',
        },
        {
          question: 'Can you help me find reliable contractors?',
          answer:
            'We work with a network of vetted, reliable contractors and can recommend professionals based on your project requirements and budget. We also coordinate between different trades to ensure smooth project execution.',
        },
        {
          question: 'How often will I receive project updates?',
          answer:
            "We provide regular updates through weekly reports, site photos, and scheduled meetings. You'll always know the current project status, upcoming milestones, and any issues that need attention. Communication is key to our project management approach.",
        },
        {
          question: 'What if there are delays in the project?',
          answer:
            'While we strive to meet all deadlines, sometimes delays occur due to weather, material availability, or authority processes. We proactively communicate any potential delays and work with all parties to minimize impact on the overall timeline.',
        },
      ],
    },
  ];

  const toggleItem = (categoryIndex: number, itemIndex: number) => {
    const itemId = categoryIndex * 100 + itemIndex;
    setOpenItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const isItemOpen = (categoryIndex: number, itemIndex: number) => {
    const itemId = categoryIndex * 100 + itemIndex;
    return openItems.includes(itemId);
  };

  return (
    <>
      <SEO
        title="FAQ"
        description="Frequently asked questions about our architectural services, design process, costs, and project management."
      />

      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative py-24 bg-gradient-to-r from-slate-900 to-slate-800 overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/3825581/pexels-photo-3825581.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="FAQ Hero"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto container-padding text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Frequently Asked <span className="text-gradient">Questions</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed">
              Get answers to common questions about our architectural services,
              design process, and project management approach.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section ref={faqRef} className="section-padding">
        <div className="max-w-6xl mx-auto container-padding">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={faqInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Have <span className="text-gradient">Questions?</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Find answers to the most commonly asked questions about our services and process
            </p>
          </motion.div>

          {/* FAQ Cards */}
          <div className="space-y-12">
            {faqCategories.map((category, cIdx) => (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 50 }}
                animate={faqInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: cIdx * 0.1 }}
                className="bg-white dark:bg-gray-900 rounded-3xl shadow-lg p-6 md:p-8"
              >
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
                  {category.title}
                </h3>
                <div className="space-y-4">
                  {category.faqs.map((faq, fIdx) => (
                    <div
                      key={fIdx}
                      className="border-b border-gray-200 dark:border-gray-700 last:border-b-0"
                    >
                      <button
                        onClick={() => toggleItem(cIdx, fIdx)}
                        className="w-full py-4 flex justify-between items-center text-left hover:text-yellow-600 transition-colors"
                      >
                        <span className="text-lg font-medium text-gray-900 dark:text-white">
                          {faq.question}
                        </span>
                        {isItemOpen(cIdx, fIdx) ? (
                          <Minus className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                        ) : (
                          <Plus className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                        )}
                      </button>
                      <AnimatePresence>
                        {isItemOpen(cIdx, fIdx) && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <p className="py-2 text-gray-600 dark:text-gray-400">{faq.answer}</p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Still Have Questions Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={faqInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-16 bg-gradient-to-r from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 p-8 rounded-3xl shadow-xl text-center"
          >
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Still Have Questions?
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-10 text-lg max-w-2xl mx-auto">
              Can't find the answer you're looking for? Our team is here to help. Contact us directly and we'll get back to you as soon as possible.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center flex-wrap">
              <a
                href={`tel:${process.env.REACT_APP_PHONE_NUMBER || '+911234567890'}`}
                className="flex items-center justify-center w-full sm:w-auto px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-700 hover:scale-105 transform transition-all duration-300 rounded-full shadow-lg text-white font-semibold"
              >
                <Phone className="w-6 h-6 mr-2" />
                Call Now
              </a>
              <a
                href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || '911234567890'}?text=Hi! I have a question about your architectural services.`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-full sm:w-auto px-6 py-4 bg-gradient-to-r from-green-400 to-green-600 hover:scale-105 transform transition-all duration-300 rounded-full shadow-lg text-white font-semibold group"
              >
                <MessageCircle className="w-6 h-6 mr-2 transition-transform duration-300 group-hover:animate-bounce" />
                WhatsApp
              </a>
              <a
                href={`mailto:${process.env.REACT_APP_EMAIL || 'brajdecoranddesigner@gmail.com'}`}
                className="flex items-center justify-center w-full sm:w-auto px-6 py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:scale-105 transform transition-all duration-300 rounded-full shadow-lg text-white font-semibold"
              >
                <Mail className="w-6 h-6 mr-2" />
                Email Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact CTA Section - Full Width */}
      <section className="w-full py-17 bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-400">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={faqInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center px-4 sm:px-6 lg:px-8"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl md:text-2xl text-yellow-100 mb-10 max-w-3xl mx-auto leading-relaxed">
            Let's discuss your architectural needs and create something extraordinary together.
          </p>
          <a
            href="/contact"
            className="inline-block bg-white text-yellow-600 hover:bg-gray-50 font-semibold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
          >
            Get Free Consultation
          </a>
        </motion.div>
      </section>
    </>
  );
};

export default FAQ;